/**
 * 标签管理页面
 * 创建、编辑、删除标签，查看标签统计
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Layout, Card, Button, Table, Tag, Space, Modal, Form, Input,
  message, Popconfirm, Typography, Statistic, Row, Col, ColorPicker
} from 'antd'
import {
  TagOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  ArrowLeftOutlined, FileTextOutlined
} from '@ant-design/icons'
import type { Color } from 'antd/es/color-picker'
import { api } from '../services/api'
import './Tags.css'

const { Header, Content } = Layout
const { Title, Text } = Typography

interface TagItem {
  id: number
  name: string
  color: string
  description: string | null
  article_count: number
  created_at: string
}

const Tags: React.FC = () => {
  const navigate = useNavigate()
  const [tags, setTags] = useState<TagItem[]>([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingTag, setEditingTag] = useState<TagItem | null>(null)
  const [form] = Form.useForm()
  const [selectedColor, setSelectedColor] = useState<string>('#D97706')

  useEffect(() => {
    loadTags()
  }, [])

  const loadTags = async () => {
    try {
      setLoading(true)
      const response = await api.tags.list({ page_size: 100 })
      
      if (response.success) {
        setTags(response.data || [])
      }
    } catch (error: any) {
      console.error('加载标签失败:', error)
      message.error('加载标签失败')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingTag(null)
    setSelectedColor('#D97706')
    form.resetFields()
    setModalVisible(true)
  }

  const handleEdit = (tag: TagItem) => {
    setEditingTag(tag)
    setSelectedColor(tag.color || '#D97706')
    form.setFieldsValue({
      name: tag.name,
      description: tag.description,
      color: tag.color
    })
    setModalVisible(true)
  }

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        name: values.name,
        description: values.description,
        color: selectedColor
      }

      if (editingTag) {
        // 更新标签
        const response = await api.tags.update(editingTag.id, data)
        if (response.success) {
          message.success('更新成功')
        }
      } else {
        // 创建标签
        const response = await api.tags.create(data)
        if (response.success) {
          message.success('创建成功')
        }
      }

      setModalVisible(false)
      loadTags()
    } catch (error: any) {
      console.error('保存标签失败:', error)
      message.error(editingTag ? '更新失败' : '创建失败')
    }
  }

  const handleDelete = async (id: number) => {
    try {
      const response = await api.tags.delete(id)
      
      if (response.success) {
        message.success('删除成功')
        loadTags()
      }
    } catch (error: any) {
      console.error('删除标签失败:', error)
      message.error('删除失败')
    }
  }

  const handleColorChange = (color: Color) => {
    setSelectedColor(color.toHexString())
  }

  const columns = [
    {
      title: '标签名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: TagItem) => (
        <Tag color={record.color || 'blue'} style={{ fontSize: 14, padding: '4px 12px' }}>
          {text}
        </Tag>
      )
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || <Text type="secondary">无描述</Text>
    },
    {
      title: '文章数量',
      dataIndex: 'article_count',
      key: 'article_count',
      render: (count: number) => (
        <Space>
          <FileTextOutlined />
          <Text>{count || 0}</Text>
        </Space>
      ),
      sorter: (a: TagItem, b: TagItem) => (a.article_count || 0) - (b.article_count || 0)
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => new Date(text).toLocaleDateString('zh-CN')
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: TagItem) => (
        <Space>
          <Button
            type="link"
            icon={<FileTextOutlined />}
            onClick={() => navigate(`/articles?tag_id=${record.id}`)}
          >
            查看关联文章
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确认删除"
            description={`确定要删除标签"${record.name}"吗？`}
            onConfirm={() => handleDelete(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  const totalArticles = tags.reduce((sum, tag) => sum + (tag.article_count || 0), 0)

  return (
    <Layout className="tags-layout">
      <Header className="tags-header">
        <div className="header-left">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            type="text"
            style={{ color: 'white' }}
          >
            返回
          </Button>
          <Title level={3} style={{ color: 'white', margin: 0 }}>
            <TagOutlined /> 标签管理
          </Title>
        </div>

        <div className="header-right">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            style={{ background: 'var(--surface)', color: 'var(--brand)' }}
          >
            新建标签
          </Button>
        </div>
      </Header>

      <Content className="tags-content">
        {/* 统计卡片 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="标签总数"
                value={tags.length}
                prefix={<TagOutlined />}
                valueStyle={{ color: 'var(--fg)' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="关联文章"
                value={totalArticles}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: 'var(--fg)' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="平均文章数"
                value={tags.length > 0 ? (totalArticles / tags.length).toFixed(1) : 0}
                suffix="篇/标签"
                valueStyle={{ color: 'var(--fg)' }}
              />
            </Card>
          </Col>
        </Row>

        {/* 标签表格 */}
        <Card>
          <Table
            columns={columns}
            dataSource={tags}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 个标签`
            }}
          />
        </Card>
      </Content>

      {/* 创建/编辑模态框 */}
      <Modal
        title={editingTag ? '编辑标签' : '新建标签'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="标签名称"
            rules={[
              { required: true, message: '请输入标签名称' },
              { max: 20, message: '标签名称最多20个字符' }
            ]}
          >
            <Input placeholder="输入标签名称" prefix={<TagOutlined />} />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
          >
            <Input.TextArea
              placeholder="输入标签描述（可选）"
              rows={3}
              maxLength={200}
              showCount
            />
          </Form.Item>

          <Form.Item
            label="标签颜色"
          >
            <Space>
              <ColorPicker
                value={selectedColor}
                onChange={handleColorChange}
                showText
              />
              <Tag color={selectedColor} style={{ fontSize: 14, padding: '4px 12px' }}>
                {form.getFieldValue('name') || '预览'}
              </Tag>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  )
}

export default Tags

